
import { Clock, CreditCard, Star } from "lucide-react";
import { PrimeseatLogo } from "@shared/components/common";
import { verifyAttrs } from "@/shared/verify/core/contract";

interface TimeSlotButtonProps {
  time: string;
  selected: boolean;
  onClick: () => void;
  isPremium?: boolean;
  /** Original slot type: 'deposit' | 'no_show' | 'Yes' etc. */
  type?: string;
  /** Slot has an event menu (star icon, takes priority over guarantee) */
  hasEventMenu?: boolean;
}

/**
 * Pick the slot icon based on type. Priority: event star > guarantee card > clock.
 * Matches legacy behaviour (checkSlotSpecialEvent takes priority over deposit/no_show).
 */
function SlotIcon({ type, hasEventMenu, selected }: { type?: string; hasEventMenu?: boolean; selected: boolean }) {
  const color = selected ? "text-brand-foreground" : "text-brand";

  if (hasEventMenu) {
    return <Star size={18} className={color} />;
  }
  if (type === 'deposit' || type === 'no_show') {
    return <CreditCard size={18} className={color} />;
  }
  return <Clock size={18} className={color} />;
}

const TimeSlotButton = ({ time, selected, onClick, isPremium, type, hasEventMenu }: TimeSlotButtonProps) => (
  <div className={`w-full max-w-xs mx-auto relative ${isPremium ? 'mb-8' : ''}`}>
    <button
      type="button"
      data-testid={`time-slot-${time}`}
      {...verifyAttrs("TimeSlotButton", {
        time,
        selected,
        isPremium: Boolean(isPremium),
        type: type ?? "default",
        hasEventMenu: Boolean(hasEventMenu),
      })}
      className={`
        w-full px-4 py-4 rounded-xl border shadow-sm transition active:scale-[0.96] flex items-center justify-center gap-2
        focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1
        ${selected
          ? "bg-brand text-brand-foreground border-brand"
          : "bg-card text-foreground border-border hover:border-brand hover:text-brand"
        }
      `}
      onClick={onClick}
      aria-pressed={selected}
    >
      <SlotIcon type={type} hasEventMenu={hasEventMenu} selected={selected} />
      <span className="text-lg font-medium">{time}</span>
    </button>
    {isPremium && (
      // Decoration: it overlaps the button's tap target, so it must never take
      // a pointer event.
      <div
        data-testid="premium-badge"
        className="absolute top-full -translate-y-2 left-1/2 -translate-x-1/2 z-10 pointer-events-none"
      >
        <PrimeseatLogo />
      </div>
    )}
  </div>
);

export default TimeSlotButton;
